import { get, post } from '../../../DAL/data-provider';
import { IServerValidation } from '../../../domain/validation/interfaces';
import { CreateAccountReferrer } from '../enums/create-account-referrer';
import { ILocalDataRequest } from '../../shopping-cart/cart/interfaces';
import { IsAuthReferrer } from '../enums/is-auth-referrer';
import { SELECTED_PAYMENT_TAB_STORAGE_KEY } from '../../shopping-cart/cart/domain/shopping-cart-provider';
import { ExternalLoginServiceEnum } from '../enums/external-service-enum';
import { googleSignOut } from '../../../domain/google/google-login-provider';
import { facebookLogOut } from '../../../domain/facebook/facebook-login-provider';
import { getSettingFromHtml } from '../../../domain/common';

export enum ILoginReferrer {
    LoginPage = 0,
    CartPage = 1,
    MainExternalLogin = 5,
    CartExternalLogin = 6,
}

/**
 * perform login
 */
export const login = async (
    referrer: ILoginReferrer,
    userName: string,
    password: string,
    isPersistent: boolean,
    captchaCode: string,
    recaptchaVersion: number,
    localData?: ILocalDataRequest,
    wavesAppCode = '',
    wavesAppTimeToken = 0,
    wavesUserGuid = '',
    wavesAppLoginToken = ''
) : Promise<{data: IServerValidation, statusCode: number }> => {

    return post('/api/membership/login', {
        userName,
        password,
        isPersistent,
        captchaCode,
        recaptchaVersion,

        referrer,
        shipAddressLine: localData?.shipAddressLine,
        shipCity: localData?.shipCity,
        shipZipCode: localData?.shipZipCode,
        shipCountry2LetterCode: localData?.shipCountry2LetterCode,
        shipState2LetterCode: localData?.shipState2LetterCode,
        shippingCode: localData?.shippingCode,

        taxCountry2LetterCode: localData?.taxCountry2LetterCode,
        taxState2LetterCode: localData?.taxState2LetterCode,
        taxZipCode: localData?.taxZipCode,

        wavesAppCode,
        wavesAppTimeToken,
        wavesUserGuid,
        wavesAppLoginToken,
    });
};

export const externalLogin = async (
    externalService: ExternalLoginServiceEnum,
    accessToken: string,
    userID: string,
    email: string,
    name: string,
    referrer: ILoginReferrer,
    isPersistent: boolean,
    isShoppingCart: boolean,

    localData?: ILocalDataRequest,
    wavesAppCode = '',
    wavesAppTimeToken = 0,
    wavesUserGuid = '',
    wavesAppLoginToken = '',

    // message gears -------
    pageNodeGUID?: string,
    firstUTMSource?: string,
    firstUTMMedium?: string,
    firstUTMCampaign?: string,
    lastUTMSource?: string,
    lastUTMMedium?: string,
    lastUTMCampaign?: string
) : Promise<{ data: IServerValidation, statusCode: number }> => {

    return post('/api/membership/external-login', {
        externalService,
        accessToken,
        userID,
        email,
        name,

        isPersistent,
        isShoppingCart,
        referrer,

        shipAddressLine: localData?.shipAddressLine,
        shipCity: localData?.shipCity,
        shipZipCode: localData?.shipZipCode,
        shipCountry2LetterCode: localData?.shipCountry2LetterCode,
        shipState2LetterCode: localData?.shipState2LetterCode,
        shippingCode: localData?.shippingCode,

        taxCountry2LetterCode: localData?.taxCountry2LetterCode,
        taxState2LetterCode: localData?.taxState2LetterCode,
        taxZipCode: localData?.taxZipCode,

        wavesAppCode,
        wavesAppTimeToken,
        wavesUserGuid,
        wavesAppLoginToken,

        pageNodeGUID,
        firstUTMSource,
        firstUTMMedium,
        firstUTMCampaign,
        lastUTMSource,
        lastUTMMedium,
        lastUTMCampaign,
    });
};

/**
 * perform create account
 */
export const createAccount = async (
    referrer: CreateAccountReferrer,
    email: string,
    password: string,
    captchaCode: string,
    recaptchaVersion: number,

    // message gears -------
    pageNodeGUID?: string,
    firstUTMSource?: string,
    firstUTMMedium?: string,
    firstUTMCampaign?: string,
    lastUTMSource?: string,
    lastUTMMedium?: string,
    lastUTMCampaign?: string
) : Promise<{data: IServerValidation, statusCode: number }> => {

    return post('/api/membership/create-account', {
        referrer,
        email,
        password,
        captchaCode,
        recaptchaVersion,
        pageNodeGUID,
        productGUID: getSettingFromHtml('product-guid'),
        firstUTMSource,
        firstUTMMedium,
        firstUTMCampaign,
        lastUTMSource,
        lastUTMMedium,
        lastUTMCampaign,
    });
};

export const logout = async () => {
    try{
        window.sessionStorage.removeItem(SELECTED_PAYMENT_TAB_STORAGE_KEY);
    }
    catch(ex){}

    try{
        googleSignOut();
    }
    catch (ex){}

    try{
        facebookLogOut();
    }
    catch (ex){}

    return post('/api/membership/logout', {});
};

export const logoutFromAllDevices = async () => {
    try{
        window.sessionStorage.removeItem(SELECTED_PAYMENT_TAB_STORAGE_KEY);
    }
    catch(ex){}

    try{
        googleSignOut();
    }
    catch (ex){}

    try{
        facebookLogOut();
    }
    catch (ex){}

    return post('/api/membership/logout-all-devices', {});
};

/**
 * resend activation email
 */
export const resendActivationEmail = async (captchaCode: string, recaptchaVersion: number) : Promise<{data: IServerValidation, statusCode: number }> => {
    return post('/api/membership/resend-activation-email', {
        captchaCode,
        recaptchaVersion,
    });
};

/**
 * update email
 */
export const updateEmail = async (
    newEmail: string,
    captchaCode: string,
    recaptchaVersion: number,
) : Promise<{data: IServerValidation, statusCode: number }> => {

    return post('/api/membership/update-email', {
        newEmail,
        captchaCode,
        recaptchaVersion,
    });
};

/**
 * get email preferences
 */
export const getEmailPreferences = async (recipientGUID?: string) => {

    let url = '/api/messagegears/email-preferences';

    if(recipientGUID){
        url = `${ url }?guid=${ recipientGUID }`;
    }

    return get(url);
};

/**
 * update email preferences
 */
export const setEmailPreferences = async (recipientGUID: string, newsletterID: number, isSelected: boolean) : Promise<{data: IServerValidation, statusCode: number }> => {
    return post('/api/messagegears/set-email-preferences', {
        recipientGUID,
        newsletterID,
        isSelected
    });
};

/**
 * unsubscribe from all newsletters
 * https://dev13.waves.com/email-preferences?guid=9C12F487-58F3-478A-AC28-9D01F2557A49&unsub=http%3A%2F%2Ftrack.waves-audio.com%2Fuc%2F3%2FeyJhaSI6MjczNjMyNjEsImUiOiJpZGFuQHdhdmVzLmNvbSIsInJpIjoiNCIsInJxIjoiMDItdDIyMDgxLWQyNjE2YTAzZjU4NjQ5OTViMWIzMDIxYzcyZmY2ZDM1IiwicGgiOm51bGwsIm0iOmZhbHNlfQ%2F6L_jR0-bdCQpbPebxD8fOA&utm_campaifgn=unsubscribe
 */
export const unsubscribe = async (unsub: string, recipientGUID: string) => {
    return post('/api/messagegears/unsubscribe', {
        recipientGUID,
        unsub,
    });
};

/**
 * check if user is authenticated
 */
export const isAuthenticated = async (referrer: IsAuthReferrer, localData?: ILocalDataRequest) => {
    return post('/api/membership/is-authenticated', {
        referrer,

        shipAddressLine: localData?.shipAddressLine,
        shipCity: localData?.shipCity,
        shipZipCode: localData?.shipZipCode,
        shipCountry2LetterCode: localData?.shipCountry2LetterCode,
        shipState2LetterCode: localData?.shipState2LetterCode,
        shippingCode: localData?.shippingCode,

        taxCountry2LetterCode: localData?.taxCountry2LetterCode,
        taxState2LetterCode: localData?.taxState2LetterCode,
        taxZipCode: localData?.taxZipCode,

        taxID: localData?.taxID,
    });
};

/**
 * change password
 */
export const changePassword = async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
) : Promise<{data: IServerValidation, statusCode: number }> => {

    return post('/api/membership/change-password', {
        currentPassword,
        newPassword,
        confirmPassword,
    });
};

/**
 * forgot password
 */
export const forgotPassword = async (
    userNameOrEmail: string,
    isEmail: boolean,
    captchaCode: string,
    recaptchaVersion: number,
) : Promise<{data: IServerValidation, statusCode: number }> => {

    return post('/api/membership/forgot-password', {
        userNameOrEmail,
        isEmail,
        captchaCode,
        recaptchaVersion,
    });
};

export const validateResetPassword = async (isCancel: boolean, hash: string, id: string) => {
    return get('/api/membership/reset-password', {
        isCancel,
        hash,
        id,
    });
};

export const resetPassword = async (
    hash: string,
    id: string,
    password: string,
    confirmPassword: string,
    captchaCode: string,
    recaptchaVersion: number,
) : Promise<{data: IServerValidation, statusCode: number }> => {

    return post('/api/membership/reset-password', {
        hash,
        id,
        password,
        confirmPassword,
        captchaCode,
        recaptchaVersion,
    });
};

export const sendPluginsBIConsent = async () => {
    return post('/api/membership/pluginsbi-consent', {});
};

